import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../assets/styles/irprint.css";
import { PRINT_TEXT } from "@utils/constants/print.constants";
import logo from "../../../assets/images/print_logo.png";
import { formatDBDate } from "@utils/general/format-db-date";
import useGetOneGoodsDelivery from "@hooks/goods-request/useGetOneGoodsDelivery";

const DeliveryNotePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { goodsDeliveryData } = useGetOneGoodsDelivery(id!);

  const defaultValues = {
    deliveryDate: goodsDeliveryData?.delivery_date,
    ReferenceNo: goodsDeliveryData?.reference_id,
    deliveryNo: goodsDeliveryData?.goods_delivery_note_code,
    delivery_date: goodsDeliveryData?.delivery_date,
    customer_name: goodsDeliveryData?.business_unit_name,
    phone: goodsDeliveryData?.phone_number ?? "",
    address: goodsDeliveryData?.business_unit_address ?? "",
    way_name: goodsDeliveryData?.way_name,
    invoice_date: goodsDeliveryData?.invoice_date,
    issued_by:
      goodsDeliveryData?.first_name + " " + goodsDeliveryData?.last_name,
    warehouse_name: goodsDeliveryData?.warehouse_name,
    product_detail: goodsDeliveryData?.product_detail || [],
    description: goodsDeliveryData?.description,
  };

  useEffect(() => {
    const handleAfterPrint = () => navigate(-1);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [navigate]);

  useEffect(() => {
    setTimeout(() => window.print(), 500);
  }, []);

  return (
    <div className="print-page">
      <div className="print-container relative min-h-[calc(100vh-2rem)] pb-20">
        {/* Header Section */}
        <div className="flex items-center gap-4 mb-4">
          <div className="logo-container ml-10 mb-5 w-40 h-30">
            <img
              src={logo}
              alt="Company Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="header text-center ml-10 mb-0">
            <h1 className="company-name text-xl font-bold">
              {PRINT_TEXT.companyName}
            </h1>
            <h2 className="company-name text-base font-semibold">
              {PRINT_TEXT.companyAddress}
            </h2>
          </div>
        </div>

        <div className="text-right font-bold text-xl mb-2 mr-10">
          <h2>Goods Issue Note</h2>
        </div>

        <div className="border-b border-black mb-2"></div>

        {/* Document Info */}
        <div className="document-info grid grid-cols-2 gap-4 mb-4 mr-12">
          <div className="left-col">
            <div className="info-row flex mb-2">
              <span className="label font-semibold w-40">Customer Name</span>
              <span>:</span>
              <span className="value border-b border-black flex-1 text-left pl-2">
                {defaultValues.customer_name || "-"}
              </span>
            </div>

            <div className="info-row flex mb-2">
              <span className="label font-semibold w-40">Address</span>
              <span>:</span>
              <span className="value border-b border-black flex-1 text-left pl-2">
                {defaultValues.address || ""} {defaultValues.phone || ""}
              </span>
            </div>

            <div className="info-row flex mb-2">
              <span className="label font-semibold w-40">Warehouse</span>
              <span>:</span>
              <span className="value border-b border-black flex-1 text-left pl-2">
                {defaultValues.warehouse_name || "-"}
              </span>
            </div>
          </div>

          <div className="right-col">
            <div className="info-row flex mb-2">
              <span className="label font-semibold w-40">Reference No</span>
              <span>:</span>
              <span className="value border-b border-black flex-1 text-left pl-2">
                {defaultValues.deliveryNo || "-"}
              </span>
            </div>

            <div className="info-row flex mb-2">
              <span className="label font-semibold w-40">Issue No</span>
              <span>:</span>
              <span className="value border-b border-black flex-1 text-left pl-2">
                {defaultValues.ReferenceNo || "-"}
              </span>
            </div>

            <div className="info-row flex mb-2">
              <span className="label font-semibold w-40">Issue Date</span>
              <span>:</span>
              <span className="value border-b border-black flex-1 text-left pl-2">
                {defaultValues.deliveryDate
                  ? formatDBDate(defaultValues.deliveryDate)
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <table className="items-table w-full border-collapse mb-4">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-1 text-center w-10">
                {PRINT_TEXT.goodIssueTableUtils.headers.no}
              </th>
              <th className="border p-1 text-left">
                {PRINT_TEXT.goodIssueTableUtils.headers.itemDescription}
              </th>
              <th className="border p-1 text-center">
                {PRINT_TEXT.goodIssueTableUtils.headers.unit}
              </th>
              <th className="border p-1 text-center">
                {PRINT_TEXT.goodIssueTableUtils.headers.qty}
              </th>
            </tr>
          </thead>
          <tbody>
            {defaultValues.product_detail.map((item: any, index: number) => {
              return (
                <tr key={index}>
                  <td className="border p-1 text-center">{index + 1}</td>
                  <td className="border p-1 text-left">
                    {item.product_name} ({item.product_code})
                  </td>
                  <td className="border p-1 text-center">{item.unit_name}</td>
                  <td className="border p-1 text-center">{item.issue_qty}</td>
                </tr>
              );
            })}

            <tr>
              <td
                colSpan={3}
                className="text-right p-2 bg-gray-100 font-semibold"
              >
                Total
              </td>
              <td className="border p-1 font-semibold text-center">
                {defaultValues.product_detail.reduce(
                  (sum: number, item: any) => sum + (item.issue_qty || 0),
                  0
                )}
              </td>
              <td className="border p-1"></td>
            </tr>
          </tbody>
        </table>

        <div className=" text-left mt-10">
          <div className="text-xl font-semibold mb-5">Description:</div>
          <p className=" mb-2">{defaultValues.description}</p>
        </div>
        {/* Signature Section */}
        <div className="signatures grid grid-cols-2 gap-4 mt-8">
          <div className="signature-col text-center">
            <p className="font-semibold">Issued By</p>
            <p>{defaultValues.issued_by || "-"}</p>
            <div className="border-b border-black"></div>
          </div>

          <div className="signature-col text-center">
            <p className="font-semibold ">Received By</p>
            <div className="border-b border-black h-7 mb-1"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryNotePrintPage;
